import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, Select } from '@travellegacy/nexops-design-system';
import { Loader2 } from 'lucide-react';
import { operationalCostsAPI } from '@/shared/lib/api';
import { formatCurrency } from '@/shared/lib/utils';
import { FREQUENCY_LABELS, formatIsoDate, type RecurrenceFrequency } from '../utils/recurrence';

type CostState = Record<string, unknown>;

interface AuditEntry {
  id: string;
  costId: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE' | string;
  createdAt: string;
  user: { id: string; name: string } | null;
  before: CostState | null;
  after: CostState | null;
  metadata: { environment?: string; scope?: string; generatedLaunches?: number; updatedLaunches?: number; removedLaunches?: number; endDate?: string; effectiveFrom?: string } | null;
}

const CATEGORY_LABELS: Record<string, string> = {
  TOOLS: 'Ferramentas',
  EVENTS: 'Eventos',
  OFFICE: 'Escritório',
  SERVICES: 'Serviços',
  MARKETING: 'Marketing',
  REFUNDS: 'Prejuízos e Reembolsos',
  TAXES: 'Impostos e Encargos',
};

// Campos exibidos na comparação antes/depois, na ordem
const FIELDS: { key: string; label: string; format: (value: unknown) => string }[] = [
  { key: 'title', label: 'Título', format: (v) => String(v ?? '—') },
  { key: 'amount', label: 'Valor', format: (v) => (typeof v === 'number' ? formatCurrency(v) : '—') },
  { key: 'paymentDate', label: 'Data', format: (v) => (v ? formatIsoDate(String(v)) : '—') },
  { key: 'category', label: 'Categoria', format: (v) => CATEGORY_LABELS[String(v)] ?? String(v ?? '—') },
  { key: 'recurrenceFrequency', label: 'Periodicidade', format: (v) => (v ? FREQUENCY_LABELS[v as RecurrenceFrequency] : '—') },
  { key: 'recurringDayOfMonth', label: 'Dia', format: (v) => (v ? String(v) : '—') },
  { key: 'recurrenceEndedAt', label: 'Último dia', format: (v) => (v ? formatIsoDate(String(v)) : '—') },
  { key: 'paymentMethod', label: 'Forma de pagamento', format: (v) => String(v || '—') },
  { key: 'description', label: 'Descrição', format: (v) => String(v || '—') },
  { key: 'notes', label: 'Observações', format: (v) => String(v || '—') },
];

function actionInfo(entry: AuditEntry): { label: string; className: string } {
  if (entry.metadata?.scope === 'RECURRENCE') {
    return { label: 'Recorrência editada', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' };
  }
  if (entry.metadata?.scope === 'RECURRENCE_END') {
    return { label: 'Recorrência encerrada', className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' };
  }
  if (entry.action === 'CREATE') {
    return { label: 'Criação', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' };
  }
  if (entry.action === 'DELETE') {
    return { label: 'Exclusão', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' };
  }
  return { label: 'Edição', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' };
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function extraInfo(entry: AuditEntry): string | null {
  const m = entry.metadata;
  if (!m) return null;
  if (m.scope === 'RECURRENCE') return `${m.updatedLaunches ?? 0} lançamento(s) atualizado(s) a partir de ${m.effectiveFrom ? formatIsoDate(m.effectiveFrom) : 'hoje'}`;
  if (m.scope === 'RECURRENCE_END') return `${m.removedLaunches ?? 0} lançamento(s) futuro(s) removido(s)`;
  if (entry.action === 'CREATE' && m.generatedLaunches) return `Recorrência com ${m.generatedLaunches} lançamento(s) gerado(s)`;
  return null;
}

function AuditEntryRow({ entry }: { entry: AuditEntry }) {
  const info = actionInfo(entry);
  const state = entry.after ?? entry.before ?? {};
  const changes =
    entry.action === 'UPDATE' && entry.before && entry.after
      ? FIELDS.filter((f) => JSON.stringify(entry.before![f.key] ?? null) !== JSON.stringify(entry.after![f.key] ?? null))
      : [];
  const extra = extraInfo(entry);

  return (
    <div className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-xs px-2 py-0.5 rounded font-medium ${info.className}`}>{info.label}</span>
        <span className="font-medium">{String(state.title ?? '—')}</span>
        {entry.action !== 'UPDATE' && (
          <span className="text-sm text-muted-foreground">
            {FIELDS[1].format(state.amount)} · {FIELDS[2].format(state.paymentDate)}
          </span>
        )}
      </div>

      {changes.length > 0 && (
        <ul className="space-y-0.5 text-sm">
          {changes.map((field) => (
            <li key={field.key}>
              <span className="text-muted-foreground">{field.label}:</span>{' '}
              <span className="line-through text-muted-foreground">{field.format(entry.before![field.key])}</span>{' '}
              → <span className="font-medium">{field.format(entry.after![field.key])}</span>
            </li>
          ))}
        </ul>
      )}

      {extra && <p className="text-xs text-muted-foreground">{extra}</p>}

      <p className="text-xs text-muted-foreground">
        {formatDateTime(entry.createdAt)} · {entry.user?.name ?? 'Usuário desconhecido'}
        {entry.metadata?.environment === 'development' && (
          <span className="ml-2 rounded bg-yellow-100 px-1.5 py-0.5 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
            ambiente de desenvolvimento
          </span>
        )}
      </p>
    </div>
  );
}

/**
 * Histórico de alterações das despesas (geral ou de um lançamento)
 */
export function OperationalCostAuditDialog({
  open,
  onOpenChange,
  costId,
  title,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  costId?: string;
  title?: string;
}) {
  const [days, setDays] = useState(30);

  const { data: entries, isLoading } = useQuery({
    queryKey: ['operational-costs', 'audit', days, costId],
    queryFn: async () => {
      const response = await operationalCostsAPI.listAudit({ days: costId ? 365 : days, costId });
      return response.data.data as AuditEntry[];
    },
    enabled: open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title ? `Histórico: ${title}` : 'Histórico de alterações'}</DialogTitle>
        </DialogHeader>
        <div className="p-6 space-y-4">
          {!costId && (
            <div className="w-48">
              <Select
                id="audit-days"
                value={String(days)}
                onChange={(e) => setDays(Number(e.target.value))}
                options={[
                  { value: '7', label: 'Últimos 7 dias' },
                  { value: '30', label: 'Últimos 30 dias' },
                  { value: '90', label: 'Últimos 90 dias' },
                  { value: '365', label: 'Último ano' },
                ]}
              />
            </div>
          )}

          <div className="max-h-[60vh] overflow-y-auto rounded-lg border divide-y">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
            ) : !entries || entries.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Nenhuma alteração registrada no período. O histórico começa a partir desta versão.
              </p>
            ) : (
              entries.map((entry) => <AuditEntryRow key={entry.id} entry={entry} />)
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
